import React from 'react';
import { motion } from 'framer-motion';

const VOCAInsights = ({ analyticsResults, loading = false }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading VOCA insights...</p>
        </div>
      </div>
    );
  }

  if (!analyticsResults || Object.keys(analyticsResults).length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 text-6xl mb-4">🔍</div>
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">No VOCA Analytics Available</h3>
        <p className="text-gray-600 dark:text-gray-400">
          Complete a VOCA training task to generate insights and analytics.
        </p>
      </div>
    );
  }

  const { topicInsights, operationalDashboard, executiveDashboard, recommendations, enhancedResults } = analyticsResults;

  return (
    <div className="space-y-6">
      {/* Executive Dashboard */}
      {executiveDashboard?.analytics_result && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6"
        >
          <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            📊 Executive Dashboard
          </h3>
          
          {/* Executive Summary */}
          {executiveDashboard.analytics_result.executive_summary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {executiveDashboard.analytics_result.executive_summary.total_feedback_analyzed}
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-300">Total Feedback Analyzed</div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {executiveDashboard.analytics_result.executive_summary.net_sentiment_score}
                </div>
                <div className="text-sm text-green-700 dark:text-green-300">Net Sentiment Score</div>
              </div>
              <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {executiveDashboard.analytics_result.executive_summary.customer_health_score}
                </div>
                <div className="text-sm text-orange-700 dark:text-orange-300">Customer Health Score</div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {executiveDashboard.analytics_result.executive_summary.critical_issues_count}
                </div>
                <div className="text-sm text-red-700 dark:text-red-300">Critical Issues</div>
              </div>
            </div>
          )}

          {/* Top Priority Areas */}
          {executiveDashboard.analytics_result.executive_summary?.top_priority_areas && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">🎯 Top Priority Areas</h4>
              <div className="space-y-2">
                {executiveDashboard.analytics_result.executive_summary.top_priority_areas.map((area, index) => (
                  <div key={index} className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 rounded-r-lg">
                    <p className="text-gray-700 dark:text-gray-300">{area}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Critical Alerts */}
          {executiveDashboard.analytics_result.critical_alerts && executiveDashboard.analytics_result.critical_alerts.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">🚨 Critical Alerts</h4>
              <div className="space-y-2">
                {executiveDashboard.analytics_result.critical_alerts.map((alert, index) => (
                  <div key={index} className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <h5 className="font-semibold text-red-700 dark:text-red-300">{alert.title}</h5>
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1">{alert.description}</p>
                    {alert.action_required && (
                      <span className="inline-block mt-2 px-2 py-1 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-xs rounded">
                        Action Required
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Strategic Insights */}
      {executiveDashboard?.analytics_result?.strategic_insights && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6"
        >
          <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            🎯 Strategic Insights
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Customer Concerns */}
            {executiveDashboard.analytics_result.strategic_insights.top_customer_concerns && (
              <div>
                <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">⚠️ Top Customer Concerns</h4>
                <div className="space-y-2">
                  {executiveDashboard.analytics_result.strategic_insights.top_customer_concerns.slice(0, 5).map((concern, index) => (
                    <div key={index} className="flex items-center p-2 bg-red-50 dark:bg-red-900/20 rounded">
                      <span className="w-6 h-6 bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center text-xs font-semibold mr-3">
                        {index + 1}
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 capitalize">{concern}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Customer Strengths */}
            {executiveDashboard.analytics_result.strategic_insights.top_customer_strengths && (
              <div>
                <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">✅ Top Customer Strengths</h4>
                <div className="space-y-2">
                  {executiveDashboard.analytics_result.strategic_insights.top_customer_strengths.slice(0, 5).map((strength, index) => (
                    <div key={index} className="flex items-center p-2 bg-green-50 dark:bg-green-900/20 rounded">
                      <span className="w-6 h-6 bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center text-xs font-semibold mr-3">
                        {index + 1}
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 capitalize">{strength}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Growth Opportunities */}
          {executiveDashboard.analytics_result.strategic_insights.growth_opportunities && (
            <div className="mt-6">
              <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">🚀 Growth Opportunities</h4>
              <div className="space-y-2">
                {executiveDashboard.analytics_result.strategic_insights.growth_opportunities.map((opportunity, index) => (
                  <div key={index} className="p-3 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 rounded-r-lg">
                    <p className="text-gray-700 dark:text-gray-300">{opportunity}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Recommendations */}
      {recommendations?.analytics_result && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6"
        >
          <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            💡 Strategic Recommendations
          </h3>

          {recommendations.analytics_result.recommendations && (
            <div className="space-y-4">
              {recommendations.analytics_result.recommendations.slice(0, 6).map((rec, index) => (
                <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-white">{rec.title}</h4>
                    <div className="flex space-x-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        rec.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' :
                        rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' :
                        'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                      }`}>
                        {rec.priority?.toUpperCase()}
                      </span>
                      {rec.quick_win && (
                        <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-xs font-semibold">
                          QUICK WIN
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-3">{rec.description}</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Business Impact:</span>
                      <span className="ml-1 text-gray-600 dark:text-gray-400 capitalize">{rec.business_impact}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Effort:</span>
                      <span className="ml-1 text-gray-600 dark:text-gray-400 capitalize">{rec.implementation_effort}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Affected Comments:</span>
                      <span className="ml-1 text-gray-600 dark:text-gray-400">{rec.affected_comments_count}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Topic Insights */}
      {/* {topicInsights?.analytics_result && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6"
        >
          <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            🏷️ Topic Insights
          </h3>

          {topicInsights.analytics_result.topics && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {topicInsights.analytics_result.topics.slice(0, 8).map((topic, index) => (
                <div key={index} className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-md font-semibold text-gray-800 dark:text-white">{topic.topic}</h4>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {topic.count} mentions
                    </span>
                  </div>
                  <div className="flex items-center mb-2">
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          topic.sentiment_score > 0 ? 'bg-green-500' : 
                          topic.sentiment_score < 0 ? 'bg-red-500' : 'bg-yellow-500'
                        }`}
                        style={{ width: `${Math.min(Math.abs(topic.sentiment_score || 0) * 100, 100)}%` }}
                      />
                    </div>
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                      {topic.sentiment_score > 0 ? '+' : ''}{(topic.sentiment_score || 0).toFixed(2)}
                    </span>
                  </div>
                  {topic.keywords && topic.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {topic.keywords.slice(0, 3).map((keyword, kidx) => (
                        <span key={kidx} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded">
                          {keyword}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )} */}

      {/* Enhanced Results Summary */}
      {enhancedResults?.analysis_summary && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6"
        >
          <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            📈 Analysis Overview
          </h3>

          {/* Sentiment Distribution */}
          {enhancedResults.analysis_summary.sentiment_distribution && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">Sentiment Distribution</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(enhancedResults.analysis_summary.sentiment_distribution).map(([sentiment, count]) => (
                  <div key={sentiment} className={`p-3 rounded-lg text-center ${
                    sentiment === 'positive' ? 'bg-green-50 dark:bg-green-900/20' :
                    sentiment === 'negative' ? 'bg-red-50 dark:bg-red-900/20' :
                    sentiment === 'neutral' ? 'bg-gray-50 dark:bg-gray-700' :
                    'bg-blue-50 dark:bg-blue-900/20'
                  }`}>
                    <div className={`text-2xl font-bold ${
                      sentiment === 'positive' ? 'text-green-600 dark:text-green-400' :
                      sentiment === 'negative' ? 'text-red-600 dark:text-red-400' :
                      sentiment === 'neutral' ? 'text-gray-600 dark:text-gray-400' :
                      'text-blue-600 dark:text-blue-400'
                    }`}>
                      {count}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">{sentiment}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Intent Distribution */}
          {enhancedResults.analysis_summary.intent_distribution && (
            <div>
              <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">Intent Distribution</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(enhancedResults.analysis_summary.intent_distribution).map(([intent, count]) => (
                  <div key={intent} className="p-2 bg-gray-50 dark:bg-gray-700 rounded text-center">
                    <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">{count}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 capitalize">{intent.replace('_', ' ')}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default VOCAInsights; 